import { ref, computed, watch } from "vue";
import { useCustomerApi } from "@/services/api/useCustomerApi";
import { useOrganizationBypass } from "@/composables/useOrganizationBypass";
import { useInvoiceRecipient } from "@/composables/useInvoiceRecipient";
import type { CustomerIntegration } from "@/stores/tender/types";
import type {
  InvoiceRecipientParams,
  TenderInvoiceData,
} from "@/composables/useInvoiceRecipient";

export interface InvoiceRecipientLogicParams {
  orgNumber: string;
  integrationSource: string | null;
  invoiceRecipientParams: InvoiceRecipientParams | null;
  tenderData?: TenderInvoiceData;
  bookingNumber?: string | number;
  tenderBypassReally?: boolean; // Add tender-level bypass flag
}

export const useInvoiceRecipientLogic = () => {
  const { getCustomerIntegrations } = useCustomerApi();
  const { bypassReallyInvoices, fetchOrganizationBypass } =
    useOrganizationBypass();
  const invoiceRecipientComposable = useInvoiceRecipient();

  const isLoading = ref(false);
  const customerIntegrations = ref<CustomerIntegration[]>([]);
  const integrationSource = ref<string>("");
  const lastInvoiceRecipientParams = ref<InvoiceRecipientParams | null>(null);
  const lastTenderData = ref<TenderInvoiceData | null>(null);
  const lastBookingNumber = ref<string | number | null>(null);
  const tenderBypassReally = ref<boolean>(false);
  const currentRecipientStep = ref<1 | 2 | 3 | 4 | null>(null);

  /**
   * Get integration source from accepted service offer
   */
  const getIntegrationSource = () => {
    // This will be set from the parent component
    return integrationSource.value;
  };

  /**
   * Check if integration bypass is enabled based on:
   * 1. Integration source from accepted service offer
   * 2. Customer integration values for byPassInvoicing
   * 3. Default to famac integration if no matching integration found (except for fdvWeb)
   */
  const hasIntegrationBypass = computed(() => {
    if (!customerIntegrations.value.length) return false;

    const integrationSourceValue = getIntegrationSource();

    // Find integration matching the service offer's integration source
    let targetIntegration = customerIntegrations.value.find(
      (integration) => integration.integrationSource === integrationSourceValue,
    );

    // If no matching integration, default to famac integration (but NOT for fdvWeb)
    if (!targetIntegration && integrationSourceValue !== "fdvWeb") {
      targetIntegration = customerIntegrations.value.find(
        (integration) => integration.integrationSource === "famac",
      );
    }

    // If still no integration found, return false
    if (!targetIntegration) return false;

    // Check if byPassInvoicing has a value from the target integration
    const bypassValue = targetIntegration.customerValues.find(
      (value) =>
        value.dataLabel === "byPassInvoicing" &&
        value.modelName === "organization",
    );

    // Return true only if byPassInvoicing exists AND its value is exactly "true"
    return bypassValue?.value === "true";
  });

  /**
   * Determine final bypass setting based on scenarios:
   * 1. Organization bypass false, integration false => Default Really! (false)
   * 2. Organization bypass true, integration true => Custom recipient (true)
   * 3. Organization bypass false, integration true => Custom recipient for integration requests (true)
   * 4. Organization bypass false, integration false => Default Really! (false)
   */
  const shouldBypassReally = computed(() => {
    const orgBypass = bypassReallyInvoices.value;
    const integrationBypass = hasIntegrationBypass.value;
    const tenderBypass = tenderBypassReally.value;

    // Tender-level bypass: if tender has bypass flag, allow fetching recipient data
    if (tenderBypass) {
      console.log("✅ Tender bypass: TRUE => BYPASS");
      return true;
    }

    // Scenario 2: org true, integration true => custom
    if (orgBypass && integrationBypass) {
      console.log("✅ Scenario 2: org true, integration true => BYPASS");
      return true;
    }

    // Scenario 3: org false, integration true => custom (integration overrides)
    if (!orgBypass && integrationBypass) {
      console.log("✅ Scenario 3: org false, integration true => BYPASS");
      return true;
    }

    // Scenario 1 & 4: default Really! for all other cases
    console.log("❌ Scenario 1/4: default Really! => NO BYPASS");
    return false;
  });

  /**
   * Initialize the logic by fetching organization and integration data
   */
  const initialize = async (params: InvoiceRecipientLogicParams) => {
    if (!params.orgNumber) return;

    isLoading.value = true;

    try {
      // Store tender bypass value (highest priority for bypass determination)
      tenderBypassReally.value = params.tenderBypassReally || false;

      // Set integration source from parameter
      integrationSource.value = params.integrationSource || "";

      // Store params for potential later use
      lastInvoiceRecipientParams.value = params.invoiceRecipientParams;
      lastTenderData.value = params.tenderData || null;
      lastBookingNumber.value = params.bookingNumber || null;

      // Fetch organization bypass setting
      await fetchOrganizationBypass(params.orgNumber);

      // Fetch customer integrations
      const response = await getCustomerIntegrations(params.orgNumber);
      if (response?.data) {
        customerIntegrations.value = response.data;
      }

      // Check if we should bypass Really after all data is loaded
      // We need to wait for the next tick to ensure computed values are updated
      await new Promise((resolve) => setTimeout(resolve, 0));

      // If we should bypass Really, fetch invoice recipient data
      if (shouldBypassReally.value && params.invoiceRecipientParams) {
        await invoiceRecipientComposable.fetchInvoiceRecipient(
          params.invoiceRecipientParams,
          params.tenderData,
          params.bookingNumber,
          setCurrentRecipientStep,
        );
      }
    } catch (error) {
      console.error("Error initializing invoice recipient logic:", error);
    } finally {
      isLoading.value = false;
    }
  };

  // Watch for changes in shouldBypassReally and fetch invoice recipient data when it becomes true
  watch(shouldBypassReally, async (newValue) => {
    if (
      newValue &&
      lastInvoiceRecipientParams.value &&
      !invoiceRecipientComposable.recipient.value
    ) {
      try {
        isLoading.value = true;
        await invoiceRecipientComposable.fetchInvoiceRecipient(
          lastInvoiceRecipientParams.value,
          lastTenderData.value || undefined,
          lastBookingNumber.value || undefined,
        );
      } catch (error) {
        console.error(
          "Error fetching invoice recipient on bypass change:",
          error,
        );
      } finally {
        isLoading.value = false;
      }
    }
  });

  /**
   * Get invoice recipient data based on bypass logic
   */
  const invoiceRecipientData = computed(() => {
    if (!shouldBypassReally.value) {
      return {
        recipient: null,
        isLoading: false,
        error: null,
      };
    }

    return {
      recipient: invoiceRecipientComposable.recipient.value,
      isLoading: invoiceRecipientComposable.isLoading.value || isLoading.value,
      error: invoiceRecipientComposable.error.value,
    };
  });

  const reset = () => {
    isLoading.value = false;
    customerIntegrations.value = [];
    integrationSource.value = "";
    currentRecipientStep.value = null;
  };

  /**
   * Update the current recipient step for bypass logic
   */
  const setCurrentRecipientStep = (step: 1 | 2 | 3 | 4 | null) => {
    currentRecipientStep.value = step;
  };

  return {
    isLoading,
    shouldBypassReally,
    hasIntegrationBypass,
    invoiceRecipientData,
    initialize,
    reset,
    setCurrentRecipientStep,
  };
};
